/**
 * Which languages this template ships, and how they behave.
 *
 * The four match the Flutter base template's `languages: [en, fr, es, ar]`, so
 * a fork translating one product translates both clients from the same list.
 *
 * Arabic is the reason several things here are more careful than they look. It
 * is right-to-left, which is not a styling detail: it changes the document
 * direction, the meaning of "start" and "end" in every layout rule, and which
 * side an icon belongs on. Shipping it from day one means the layouts are
 * written with logical properties (`margin-inline-start`, not `margin-left`)
 * rather than retrofitted years later, which never fully works.
 */

export const SUPPORTED_LOCALES = ["en", "fr", "es", "ar"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

/**
 * The fallback, and the language keys are authored in.
 *
 * English rather than French despite this being a French-speaking team: the
 * source locale is what every other file is diffed against, and keeping it in
 * the language the code and comments already use avoids a second translation
 * step for the people maintaining the template.
 */
export const DEFAULT_LOCALE: Locale = "en";

/** Locales written right to left. */
export const RTL_LOCALES: ReadonlySet<Locale> = new Set<Locale>(["ar"]);

export function isSupported(value: string): value is Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function directionFor(locale: Locale): "ltr" | "rtl" {
  return RTL_LOCALES.has(locale) ? "rtl" : "ltr";
}

/**
 * Pick a locale from the browser's list.
 *
 * Matches on the base language, so `fr-CA`, `fr-BE` and `fr` all get French.
 * A user whose browser is set to Canadian French should not fall back to
 * English because the region tag did not match exactly — the commonest and
 * most annoying i18n bug.
 */
export function negotiateLocale(
  requested: readonly string[],
  available: readonly Locale[] = SUPPORTED_LOCALES,
): Locale {
  for (const tag of requested) {
    const base = tag.toLowerCase().split(/[-_]/)[0] ?? "";
    const match = available.find((locale) => locale === base);
    if (match) return match;
  }
  return DEFAULT_LOCALE;
}

/**
 * Apply a locale to the document.
 *
 * `lang` matters as much as `dir`: a screen reader uses it to choose a voice,
 * and a page left as `lang="en"` reads French aloud with an English accent —
 * intelligible to nobody.
 */
export function applyLocaleToDocument(locale: Locale): void {
  const root = document.documentElement;
  root.setAttribute("lang", locale);
  root.setAttribute("dir", directionFor(locale));
}
